/* VISTA DE VALIDACIÓN DE IDENTIDAD */
import { useEffect, useState } from 'react';
import {
    CheckCircle,
    XCircle,
    Eye,
    ChevronLeft,
    ChevronRight,
    Loader2,
    ShieldCheck,
    AlertCircle,
    User,
    ExternalLink,
    X,
    Circle
} from 'lucide-react';
import { apiClient } from '../../lib/api';
//import { format } from 'date-fns';

// ----------------------------------------------------------------------
// 1. Interfaces
// ----------------------------------------------------------------------
interface BackendDoc {
    userVerifyDocId: number;
    userVerifyDocTypeName: string;
    docLink: string;
    description: string;
    status: string;
}

interface BackendVerificationItem {
    userVerifyId: number;
    userId: number;
    userName: string;
    userFullName: string;
    estadoCodigo: string; // "PEN", "APR", "REJ"
    fechaSolicitud: string;
    numero: string;
    documents: BackendDoc[];
}

interface BackendDetailResponse {
    userVerifyId: number;
    userId: number;
    userVerifyStatusCode: string;
    documents: BackendDoc[];
}

// ----------------------------------------------------------------------
// 2. Componente Principal
// ----------------------------------------------------------------------
const ValidacionIdentidadPage = () => {

    // --- ESTADOS ---
    const [rows, setRows] = useState<BackendVerificationItem[]>([]);
    const [loading, setLoading] = useState(true); // Estado de carga inicial
    const [filterStatus, setFilterStatus] = useState<string>('PEN');
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState<any>(null);
    const pageSize = 10;
    const [openModal, setOpenModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState<BackendVerificationItem | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);

    // Estado para los documentos que vienen del detalle del API
    const [detailDocs, setDetailDocs] = useState<BackendDoc[]>([]);
    const [loadingDetail, setLoadingDetail] = useState(false);

    // ----------------------------------------------------------------------
    // 3. EFECTOS (CARGA DE DATOS)
    // ----------------------------------------------------------------------
    const fetchVerifications = async () => {
        setLoading(true);
        try {
            // Construimos la URL con los parámetros que requiere tu backend
            const endpoint = `GetPendingVerifications?pageNumber=${currentPage}&pageSize=${pageSize}&statusFilter=${filterStatus}`;

            const response = await apiClient.get<any>(endpoint);

            // Según tu backend, la data real suele venir en .data o ser el array directo
            const result = response?.data || response || [];
            setRows(result);
            setPagination(response.pagination);
        } catch (error) {
            console.error("Error al cargar validaciones:", error);
            setRows([]); // Limpiar tabla en caso de error
        } finally {
            setLoading(false);
        }
    };

    // Escuchamos cambios en filterStatus y currentPage
    useEffect(() => {
        fetchVerifications();
    }, [filterStatus, currentPage]);

    // Carga de Detalle de Documentos (Replicando Lógica del Modal del original)
    useEffect(() => {
        const fetchUserDocs = async () => {
            // !!targetUserId && openModal (condición del original)
            if (!selectedUser || !openModal) return;

            setLoadingDetail(true);
            try {
                // Endpoint extraído del original: /GetUserVerificationWithDocs/${userId}
                const res = await apiClient.get<any>(`GetUserVerificationWithDocs/${selectedUser.userId}`);
                const docs = res?.data?.documents || res?.documents || [];
                setDetailDocs(docs);
            } catch (error) {
                console.error("Error al cargar detalle:", error);
                // Fallback a los documentos básicos si falla la carga de detalle
                setDetailDocs(selectedUser.documents || []);
            } finally {
                setLoadingDetail(false);
            }
        };

        fetchUserDocs();
    }, [selectedUser, openModal]);

    //const displayRows = rows;

    // ----------------------------------------------------------------------
    // 4. HANDLERS
    // ----------------------------------------------------------------------
    const handleFilterChange = (status: string) => {
        setFilterStatus(status);
        setCurrentPage(1); // Resetear a la primera página al cambiar de pestaña
    };

    const handleOpenValidate = (user: BackendVerificationItem) => {
        setSelectedUser(user);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        if (isUpdating) return;
        setOpenModal(false);
        setSelectedUser(null);
        setDetailDocs([]);
    };

    const handleDecision = async (decision: 'VALIDADO' | 'RECHAZADO') => {
        if (!selectedUser) return;

        setIsUpdating(true);
        const actionCode = decision === 'VALIDADO' ? 'APR' : 'REJ';
        const comments = decision === 'VALIDADO' ? 'Validado correctamente' : 'Documentación inconsistente';

        // Construcción de la URL con parámetros
        const finalUrl = `ApproveRejectUserVerification/${selectedUser.userVerifyId}?action=${actionCode}&comments=${encodeURIComponent(comments)}`;

        try {
            // Se envía PUT con body vacío según la lógica de tu sistema administrativo
            await apiClient.put(finalUrl, {});

            // Actualización local para feedback inmediato
            setRows(prevRows => prevRows.map(row =>
                row.userVerifyId === selectedUser.userVerifyId
                    ? { ...row, estadoCodigo: actionCode }
                    : row
            ));

            handleCloseModal();
        } catch (error) {
            console.error("Error al actualizar estado:", error);
            alert("No se pudo procesar la solicitud. Por favor, intente nuevamente.");
        } finally {
            setIsUpdating(false);
        }
    };

    // --- HELPERS ---
    const getMainDocType = (user: BackendVerificationItem | null) => {
        if (user && user.documents?.length > 0) return user.documents[0].userVerifyDocTypeName;
        return 'N/A';
    };

    const getMainDoc = (user: BackendVerificationItem | null) => {
        if (user && user.documents?.length > 0) return user.documents[0];
        return { userVerifyDocTypeName: 'N/A', docLink: '', description: '---' };
    };

    const tabs = [
        { id: 'PEN', label: "Pendientes", icon: <AlertCircle size={18} /> },
        { id: 'APR', label: "Aprobados", icon: <ShieldCheck size={18} /> },
        { id: 'REJ', label: "Rechazados", icon: <XCircle size={18} /> },
    ];

    const getStatusColor = (code: string) => {
        switch (code) {
            case 'PEN': return 'text-orange-500';
            case 'APR': return 'text-green-500';
            case 'REJ': return 'text-red-500';
            default: return 'text-gray-500';
        }
    };

    const getStatusLabel = (code: string) => {
        switch (code) {
            case 'PEN': return 'Pendiente';
            case 'APR': return 'Aprobado';
            case 'REJ': return 'Rechazado';
            default: return code;
        }
    };

    return (
        <div className="space-y-6 relative">
            {/* HEADER */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-heading font-bold text-foreground">Validación de Identidad</h1>
                    <p className="text-muted-foreground mt-1">Gestiona las solicitudes de verificación de usuarios</p>
                </div>
            </div>

            {/* TABS DE FILTROS - Usamos el nuevo handler */}
            <div className="border-b border-border">
                <div className="flex gap-8 overflow-x-auto no-scrollbar">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => handleFilterChange(tab.id)}
                            className={`flex items-center gap-2 py-4 px-1 border-b-2 text-sm font-medium transition-all ${filterStatus === tab.id
                                ? "border-primary text-primary"
                                : "border-transparent text-muted-foreground hover:text-foreground"
                                }`}
                        >
                            {tab.icon}
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="bg-card rounded-soft border border-border p-6 shadow-soft min-h-[500px]">
                {/* --- AQUÍ COMIENZA LA TABLA --- */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        {/* Cabecera de la Tabla */}
                        <thead className="border-b border-border">
                            <tr>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Estado</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Fecha Solicitud</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Tipo Documento</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Nro. Documento</th>
                                <th className="text-left py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Usuario</th>
                                <th className="text-right py-3 px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Acción</th>
                            </tr>
                        </thead>

                        {/* Cuerpo de la Tabla */}
                        <tbody className="divide-y divide-border">
                            {loading ? (
                                /* Estado: Cargando Datos */
                                <tr>
                                    <td colSpan={6} className="py-20 text-center text-muted-foreground">
                                        <div className="flex flex-col items-center justify-center">
                                            <Loader2 className="h-10 w-10 animate-spin text-primary mb-2" />
                                            <p>Cargando datos...</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : rows.length === 0 ? (
                                /* Estado: Lista Vacía (Segun el filtro del API) */
                                <tr>
                                    <td colSpan={6} className="py-20 text-center text-muted-foreground">
                                        <div className="flex flex-col items-center justify-center">
                                            <ShieldCheck className="h-10 w-10 text-muted-foreground/30 mb-2" />
                                            <p>No se encontraron solicitudes con estado: {getStatusLabel(filterStatus)}</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                /* Estado: Renderizado de Filas Reales */
                                rows.map((row) => {
                                    const displayDate = row.fechaSolicitud
                                        ? new Date(row.fechaSolicitud).toLocaleDateString('es-ES')
                                        : '---';

                                    const docType = getMainDocType(row);
                                    const isPending = row.estadoCodigo === 'PEN';

                                    return (
                                        <tr key={row.userVerifyId} className="hover:bg-muted/50 transition-colors">
                                            {/* Estado con punto de color dinámico */}
                                            <td className="py-4 px-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <Circle
                                                        size={10}
                                                        className={`fill-current ${getStatusColor(row.estadoCodigo)}`}
                                                    />
                                                    <span className="text-sm font-medium text-foreground">
                                                        {getStatusLabel(row.estadoCodigo)}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Fecha de Solicitud */}
                                            <td className="py-4 px-4 whitespace-nowrap text-sm text-muted-foreground">
                                                {displayDate}
                                            </td>

                                            {/* Tipo de Documento */}
                                            <td className="py-4 px-4 whitespace-nowrap text-sm text-foreground">
                                                {docType}
                                            </td>

                                            {/* Número de Documento */}
                                            <td className="py-4 px-4 whitespace-nowrap text-sm text-muted-foreground font-mono">
                                                {row.numero || '---'}
                                            </td>

                                            {/* Solicitante */}
                                            <td className="py-4 px-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                                                        <User size={16} />
                                                    </div>
                                                    <span className="text-sm font-medium text-foreground">{row.userFullName}</span>
                                                </div>
                                            </td>

                                            {/* Acción Botón */}
                                            <td className="py-4 px-4 whitespace-nowrap text-right">
                                                <button
                                                    onClick={() => handleOpenValidate(row)}
                                                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-soft transition-colors ${isPending
                                                        ? 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm'
                                                        : 'bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-border'
                                                        }`}
                                                >
                                                    <Eye size={14} />
                                                    {isPending ? "VALIDAR" : "VER"}
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* PAGINACIÓN DINÁMICA */}
                {pagination && pagination.totalPageCount > 1 && (
                    <div className="flex flex-col items-center gap-4 border-t border-border pt-8">
                        <div className="flex items-center gap-1">
                            <button
                                disabled={!pagination.hasPrevious}
                                onClick={() => setCurrentPage(prev => prev - 1)}
                                className="p-2.5 rounded-full hover:bg-muted text-muted-foreground disabled:opacity-30"
                            >
                                <ChevronLeft size={22} />
                            </button>

                            <span className="px-4 text-sm font-medium">
                                Página {pagination.currentPage} de {pagination.totalPageCount}
                            </span>

                            <button
                                disabled={!pagination.hasNext}
                                onClick={() => setCurrentPage(prev => prev + 1)}
                                className="p-2.5 rounded-full hover:bg-muted text-muted-foreground disabled:opacity-30"
                            >
                                <ChevronRight size={22} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* MODAL DE VALIDACIÓN */}
            {openModal && selectedUser && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-card w-full max-w-6xl max-h-[90vh] overflow-y-auto rounded-soft border border-border shadow-2xl flex flex-col relative">

                        {/* Header del Modal */}
                        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-card z-10">
                            <h2 className="text-xl font-bold text-foreground">Validación de Documentos</h2>
                            <button onClick={handleCloseModal} className="p-2 text-muted-foreground hover:bg-muted rounded-full transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        {/* Contenido del Modal */}
                        <div className="p-6 flex-1 relative">
                            {/* Loader Simulado */}
                            {(isUpdating || loadingDetail) && (
                                <div className="absolute inset-0 bg-background/80 z-20 flex flex-col items-center justify-center backdrop-blur-sm">
                                    <Loader2 className="h-10 w-10 animate-spin text-primary mb-2" />
                                    <p className="text-sm font-medium">Actualizando estado...</p>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                <div className="p-4 bg-muted/30 rounded-soft border border-border">
                                    <p className="text-sm text-muted-foreground mb-1 font-semibold">Usuario solicitante:</p>
                                    <p className="text-lg font-bold text-foreground">{selectedUser.userFullName}</p>
                                </div>
                                <div className="p-4 bg-muted/30 rounded-soft border border-border">
                                    <p className="text-sm text-muted-foreground mb-1 font-semibold">Tipo de Documento:</p>
                                    <p className="text-lg font-bold text-foreground">{getMainDoc(selectedUser).userVerifyDocTypeName}</p>
                                </div>
                            </div>

                            {/* Área de Imágenes */}
                            <div className="bg-muted/10 border border-border rounded-soft p-4 min-h-[300px] flex items-center justify-center">
                                {selectedUser.documents.length > 0 ? (
                                    <div className="flex flex-wrap justify-center gap-6 w-full">
                                        {selectedUser.documents.map((doc, index) => (
                                            <div key={index} className="flex flex-col items-center w-full sm:w-[45%]">
                                                <span className="text-xs font-bold text-primary mb-2 tracking-wider uppercase">
                                                    {index === 0 ? "ANVERSO" : "REVERSO"}
                                                </span>
                                                <div className="w-full bg-white rounded-lg border border-border shadow-sm overflow-hidden p-2">
                                                    <img
                                                        src={doc.docLink}
                                                        alt="Documento"
                                                        className="w-full h-auto max-h-[350px] object-contain"
                                                    />
                                                </div>
                                                <a href={doc.docLink} target="_blank" rel="noreferrer" className="mt-3 text-xs font-bold text-primary flex items-center gap-1 hover:underline">
                                                    Ver imagen <ExternalLink size={12} />
                                                </a>
                                            </div>
                                        ))}
                                    </div>
                                ) : (!loadingDetail &&
                                    <p className="text-muted-foreground">No hay documentos adjuntos.</p>
                                )}
                            </div>
                        </div>

                        {/* Footer / Acciones */}
                        <div className="p-6 border-t border-border bg-muted/10 flex flex-col sm:flex-row justify-between gap-4">
                            <button
                                onClick={handleCloseModal}
                                disabled={isUpdating}
                                className="px-6 py-2.5 rounded-soft border border-border bg-background text-foreground font-medium hover:bg-muted transition-colors"
                            >
                                Cerrar
                            </button>

                            {/* Solo mostrar acciones si está PENDIENTE */}
                            {selectedUser.estadoCodigo === 'PEN' && (
                                <div className="flex gap-3 w-full sm:w-auto">
                                    <button
                                        onClick={() => handleDecision('RECHAZADO')}
                                        disabled={isUpdating}
                                        className="flex-1 sm:flex-none px-6 py-2.5 rounded-soft bg-red-100 text-red-700 border border-red-200 font-bold hover:bg-red-200 transition-colors flex items-center justify-center gap-2"
                                    >
                                        <XCircle size={18} />
                                        Rechazar
                                    </button>
                                    <button
                                        onClick={() => handleDecision('VALIDADO')}
                                        disabled={isUpdating}
                                        className="flex-1 sm:flex-none px-6 py-2.5 rounded-soft bg-green-600 text-white font-bold hover:bg-green-700 transition-colors flex items-center justify-center gap-2 shadow-md"
                                    >
                                        <CheckCircle size={18} />
                                        Aprobar
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ValidacionIdentidadPage;